import React from "react";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import call, { type Output } from "./call";
import { comparatorBy, stringComparator, withNull, withOrder } from "diagnostics-lib/comparator";
import { decode, isInteger, isReal } from "diagnostics-lib/io";
import { NumberFromString, BooleanFromString } from "io-ts-types";

type ApiSetup = Output<"GET", "/projects/:projectId(uuid)/setups">["body"][number];

function decodeDiagnosisParameterValue(
  type: "INTEGER" | "FLOAT" | "BOOLEAN",
  value: string,
): number | boolean {
  switch (type) {
    case "INTEGER":
      return decode(isInteger(NumberFromString), value);
    case "FLOAT":
      return decode(isReal(NumberFromString), value);
    case "BOOLEAN":
      return decode(BooleanFromString, value);
    default: {
      const never: never = type;
      throw new Error(`Unsupported DiagnosisParameterType: ${never}`);
    }
  }
}
export enum SimulationStatus {
  VALID = "VALID",
  VALIDATING = "VALIDATING",
  INVALID = "INVALID",
  INCOMPLETE = "INCOMPLETE",
}

export enum IncompleteSimulationCauses {
  NO_MODEL = "NO_MODEL",
  NO_INPUT = "NO_INPUT",
  NO_OUTPUT = "NO_OUTPUT",
}

export enum GlobalDiagnosisStatus {
  INVALID = "INVALID",
  INCOMPLETE = "INCOMPLETE",
  COMPLETE = "COMPLETE",
}

export enum ExactDiagnosisCauses {
  NO_INPUT = "NO_INPUT",
  NO_ENABLED_INPUT = "NO_ENABLED_INPUT",
  NOT_SIMULATION_OUTPUT = "NOT_SIMULATION_OUTPUT",
  NO_FAILURE = "NO_FAILURE",
  NO_ENABLED_FAILURE = "NO_ENABLED_FAILURE",
  NO_IMPACT = "NO_IMPACT",
  MISSING_IMPACT = "MISSING_IMPACT",
}

type DiagnosisValidity = (
  | { readonly status: ExactDiagnosisCauses.NO_INPUT; readonly quantityIds?: undefined }
  | { readonly status: ExactDiagnosisCauses.NO_ENABLED_INPUT; readonly quantityIds?: undefined }
  | { readonly status: ExactDiagnosisCauses.NOT_SIMULATION_OUTPUT; readonly quantityIds: string[] }
  | { readonly status: ExactDiagnosisCauses.NO_FAILURE; readonly quantityIds?: undefined }
  | { readonly status: ExactDiagnosisCauses.NO_ENABLED_FAILURE; readonly quantityIds?: undefined }
  | { readonly status: ExactDiagnosisCauses.NO_IMPACT; readonly quantityIds?: undefined }
  | { readonly status: ExactDiagnosisCauses.MISSING_IMPACT; readonly quantityIds: string[] }
)[];
const getDiagnosisValidityCache = new WeakMap<ApiSetup, DiagnosisValidity>();
function getDiagnosisValidity(setup: ApiSetup): DiagnosisValidity {
  const cached = getDiagnosisValidityCache.get(setup);
  if (cached) return cached;
  const simulationOutputQuantityIds = setup.simulationOutputs.map(o => o.quantityId);
  const diagnosisInputs = setup.diagnosisInputs;
  const diagnosisFailures = setup.failures;

  const results = [];

  const diagnosisEnabledInputQuantityIds = diagnosisInputs
    .filter(i => i.enabled)
    .map(i => i.quantityId);
  if (diagnosisInputs.length === 0) {
    results.push({
      status: ExactDiagnosisCauses.NO_INPUT,
    } as const);
  } else if (diagnosisEnabledInputQuantityIds.length === 0) {
    results.push({
      status: ExactDiagnosisCauses.NO_ENABLED_INPUT,
    } as const);
  } else {
    const simulationOuputQuantityIdIndex = new Set(simulationOutputQuantityIds);

    const notSimulationOutputQuantityIds = diagnosisEnabledInputQuantityIds.filter(
      id => !simulationOuputQuantityIdIndex.has(id),
    );
    if (diagnosisEnabledInputQuantityIds.length > 0 && notSimulationOutputQuantityIds.length > 0) {
      results.push({
        status: ExactDiagnosisCauses.NOT_SIMULATION_OUTPUT,
        quantityIds: notSimulationOutputQuantityIds,
      } as const);
    }
  }

  const diagnosisEnabledFailures = diagnosisFailures.filter(f => f.enabled);
  if (diagnosisFailures.length === 0) {
    results.push({
      status: ExactDiagnosisCauses.NO_FAILURE,
    } as const);
  } else if (diagnosisEnabledFailures.length === 0) {
    results.push({
      status: ExactDiagnosisCauses.NO_ENABLED_FAILURE,
    } as const);
  } else {
    const diagnosisImpacts = diagnosisFailures.flatMap(f => f.impacts);
    if (diagnosisImpacts.length === 0) {
      results.push({
        status: ExactDiagnosisCauses.NO_IMPACT,
      } as const);
    } else {
      const diagnosisImpactIndex = new Set(
        diagnosisEnabledFailures.flatMap(f => f.impacts.map(i => i.quantityId)),
      );
      const missingImpactQuantityIds = diagnosisEnabledInputQuantityIds.filter(
        id => !diagnosisImpactIndex.has(id),
      );
      if (missingImpactQuantityIds.length > 0) {
        results.push({
          status: ExactDiagnosisCauses.MISSING_IMPACT,
          quantityIds: missingImpactQuantityIds,
        } as const);
      }
    }
  }

  getDiagnosisValidityCache.set(setup, results);
  return results;
}

function getDiagnosisValidityStatus(
  diagnosisStatus: { status: ExactDiagnosisCauses }[],
): GlobalDiagnosisStatus {
  if (
    diagnosisStatus.some(s =>
      [
        ExactDiagnosisCauses.NO_INPUT,
        ExactDiagnosisCauses.NO_IMPACT,
        ExactDiagnosisCauses.NO_FAILURE,
        ExactDiagnosisCauses.NO_ENABLED_INPUT,
        ExactDiagnosisCauses.NO_ENABLED_FAILURE,
      ].includes(s.status),
    )
  )
    return GlobalDiagnosisStatus.INCOMPLETE;

  if (
    diagnosisStatus.some(s =>
      [ExactDiagnosisCauses.NOT_SIMULATION_OUTPUT, ExactDiagnosisCauses.MISSING_IMPACT].includes(
        s.status,
      ),
    )
  )
    return GlobalDiagnosisStatus.INVALID;

  return GlobalDiagnosisStatus.COMPLETE;
}

type SimulationValidity =
  | readonly [SimulationStatus.INCOMPLETE, IncompleteSimulationCauses[]]
  | readonly [SimulationStatus.VALIDATING]
  | readonly [SimulationStatus.VALID]
  | readonly [SimulationStatus.INVALID];
function getSimulationValidity(setup: ApiSetup): SimulationValidity {
  if (!setup.simulationModel || !setup.simulationInputs.length || !setup.simulationOutputs.length) {
    const unexpectedStateStatus: [boolean, IncompleteSimulationCauses][] = [
      [!setup.simulationModel, IncompleteSimulationCauses.NO_MODEL],
      [!setup.simulationInputs.length, IncompleteSimulationCauses.NO_INPUT],
      [!setup.simulationOutputs.length, IncompleteSimulationCauses.NO_OUTPUT],
    ];

    return [
      SimulationStatus.INCOMPLETE,
      unexpectedStateStatus.reduce<IncompleteSimulationCauses[]>(
        (acc, [unexpected_state, cause]) => (unexpected_state ? [...acc, cause] : acc),
        [],
      ),
    ] as const;
  }

  if (setup.simulationModelValidationProcess?.status !== "DONE")
    return [SimulationStatus.VALIDATING] as const;

  if (setup.simulationModelValidationProcess?.status === "DONE")
    return setup.simulationModelSetupValid
      ? ([SimulationStatus.VALID] as const)
      : ([SimulationStatus.INVALID] as const);

  return [SimulationStatus.VALID] as const;
}

type DiagnosisParameters = { key: string; value: number | boolean }[];

export type Setup = Omit<ApiSetup, "diagnosisParameters"> & {
  simulationValidity: SimulationValidity;
  simulationStatus: SimulationStatus;
  diagnosisValidity: DiagnosisValidity;
  diagnosisStatus: GlobalDiagnosisStatus;
  diagnosisParameters: DiagnosisParameters; // overwritten
};

export type Failure = Setup["failures"][number];

const setupsCache = new WeakMap<ApiSetup[], Setup[]>();
export function useSetups(projectId: string): Setup[] {
  const getCache = useGetQueryData("/projects/:projectId(uuid)/setups", {
    routeParams: { projectId },
  });

  const readItems = React.useCallback(() => getCache()?.body, [getCache]);
  const mutateItems = useMutateBody("/projects/:projectId(uuid)/setups", {
    routeParams: { projectId },
  });

  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const { setups, deletedIds } = (
        await call("GET", "/projects/:projectId(uuid)/setups/updates", {
          routeParams: { projectId },
          query: { since: new Date(since) },
        })
      ).body;
      return [setups, deletedIds] as const;
    },
    [projectId],
  );

  useChannelUpdates(
    { key: "setups", projectId },
    {
      readItems,
      fetchUpdatedItems,
      mutateItems,
    },
  );

  const apiSetups: ApiSetup[] = useQuery("/projects/:projectId(uuid)/setups", {
    routeParams: { projectId },
  }).body;

  return React.useMemo<Setup[]>(() => {
    const cachedSetups = setupsCache.get(apiSetups);
    if (cachedSetups) return cachedSetups;
    const setups: Setup[] = apiSetups
      .map(apiSetup => {
        const simulationValidity: SimulationValidity = getSimulationValidity(apiSetup);
        const diagnosisValidity: DiagnosisValidity = getDiagnosisValidity(apiSetup);
        const simulationStatus: SimulationStatus = simulationValidity[0];
        const diagnosisStatus: GlobalDiagnosisStatus =
          getDiagnosisValidityStatus(diagnosisValidity);
        const diagnosisParameters: DiagnosisParameters = apiSetup.diagnosisParameters.map(p => ({
          key: p.key,
          value: decodeDiagnosisParameterValue(p.type, p.value),
        })); // overwritten

        const setup: Setup = {
          ...apiSetup,
          simulationValidity,
          simulationStatus,
          diagnosisValidity,
          diagnosisStatus,
          diagnosisParameters,
        };

        return setup;
      })
      .sort(comparatorBy("startDate", withNull(withOrder(stringComparator, "desc"), "last")));
    setupsCache.set(apiSetups, setups);
    return setups;
  }, [apiSetups]);
}

export function useSetup({
  projectId,
  setupId,
}: {
  projectId: string;
  setupId: string;
}): Setup | null {
  return useSetups(projectId).find(s => s.id === setupId) ?? null;
}

export function computeEndDate<S extends { startDate: null | string }>(
  setup: S,
  setups: readonly S[],
): string | null {
  const sortedSetupByDate = [...setups].sort((a, b) => {
    if (a.startDate === null) return -1;
    if (b.startDate === null) return 1;
    else return new Date(a.startDate).getTime() < new Date(b.startDate).getTime() ? -1 : 1;
  });

  const currentIndex = sortedSetupByDate.findIndex(s => s.startDate === setup.startDate);
  if (currentIndex === -1) throw new Error("Setup not found, should not happen");
  return sortedSetupByDate[currentIndex + 1]?.startDate ?? null;
}

export function getSimulationSetupErrorLogs(setup: Setup): string | null {
  const error = setup.simulationModelValidationProcess?.error;
  if (!error) return null;
  return JSON.stringify(error, null, 2);
}

export function getSimulationModelSetupOutputErrorLogs(setup: Setup): string | null {
  const output = setup.simulationModelValidationProcess?.output;
  if (!output) return null;

  return typeof output === "object" &&
    output !== null &&
    "errorCode" in output &&
    typeof output["errorCode"] === "string" &&
    output["errorCode"].trim() !== ""
    ? JSON.stringify(output, null, 2)
    : null;
}
